/**
 * Client ของ Product Master — พูดกับ `/v1/products` ตาม payload contract
 * ที่ freeze ไว้ใน `docs/API_CONTRACT.md`
 *
 * หลักการเดียวกับ `lib/permissions/client.ts`:
 * - ฟังก์ชันในไฟล์นี้ **ไม่ throw** ความล้มเหลวทุกแบบกลายเป็นสถานะที่เรียกดูได้
 *   ผู้เรียกจึงเผลอ `catch` แล้วเดาเป็น "สำเร็จ" ไม่ได้
 * - payload ที่ผิดรูปคือ `invalid-response` ไม่ใช่ "ข้อมูลว่าง" — รายการว่างกับ
 *   โหลดไม่สำเร็จต้องแยกกันให้ขาด ไม่งั้นผู้ใช้จะอ่านว่า "ไม่มีสินค้าตัวนี้ในระบบ"
 *   ทั้งที่จริงคือเรียกไม่สำเร็จ
 * - **ไม่มี fallback ที่แต่งข้อมูลขึ้นมาเอง** เซิร์ฟเวอร์ยังไม่มีบน branch นี้
 *   หน้าจอที่แสดงสินค้าปลอมตอน API ไม่ตอบ แย่กว่าหน้าจอที่บอกว่าโหลดไม่ได้
 *
 * ⚠️ ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 */

import type {
  CreateProductBody,
  CreateProductChangeRequestBody,
  CreateProductPlantInput,
  CreateProductTypeBody,
  CreateUomBody,
  Product,
  ProductChangeRequest,
  ProductChangeRequestDecision,
  ProductChangeRequestDecisionBody,
  ProductChangeRequestListPage,
  ProductImage,
  ProductImageListPage,
  ProductListFilters,
  ProductListPage,
  ProductPlantData,
  ProductTransition,
  ProductTransitionBody,
  ProductTypeEntry,
  ProductTypeListPage,
  ProductTypeTransitionBody,
  UomEntry,
  UomListPage,
  UpdateProductBody,
  UpdateProductPlantInput,
  UpdateProductTypeBody,
  UpdateUomBody,
  UploadProductImageOptions,
} from './types';
import { PRODUCT_STEEL_FIELDS } from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import { downloadFile, uploadFile } from '../http/fileTransfer';

/**
 * เส้นทางจริงของกลุ่มนี้ — ตาราง endpoint ใน contract ตัด base prefix ออก
 * แต่ URL ที่ client เรียกขึ้นต้นด้วย `/v1` เสมอ (`GET /products` →
 * `GET /v1/products`) เหมือน `ME_PERMISSIONS_PATH`
 */
export const PRODUCTS_PATH = '/v1/products';

/**
 * URL ของ product หนึ่งตัว — ใช้ `product_id` (UUID surrogate, D-039) เท่านั้น
 * **ห้ามใช้ `product_code`** เพราะรหัสธุรกิจเปลี่ยนได้ contract ระบุข้อนี้ไว้ตรง ๆ
 */
export function productPath(productId: string): string {
  return `${PRODUCTS_PATH}/${encodeURIComponent(productId)}`;
}

/**
 * URL ของ transition หนึ่งตัว — `.../{product_id}/{submit|approve|reject}`
 *
 * ชื่อ transition มาจาก `PRODUCT_TRANSITIONS` ซึ่งเป็น path segment จริง
 * ชนิดข้อมูลจึงเป็นตัวกันไม่ให้มี endpoint ที่สี่โผล่มาจากสตริงที่พิมพ์เอง
 */
export function productTransitionPath(
  productId: string,
  transition: ProductTransition,
): string {
  return `${productPath(productId)}/${transition}`;
}

/** Envelope ของ error ตาม `api/errors.py` (`error_code`, `message`, `details`) */
export interface ApiErrorEnvelope {
  readonly error_code: string | null;
  readonly message: string | null;
  /**
   * `details.rule` — วิธีที่ contract ใช้แยกกรณีทางธุรกิจออกจากกัน
   * โดยไม่เพิ่ม top-level error code ใหม่ หน้าจอต้องแสดงค่านี้ ไม่ใช่กลืนเป็น
   * "ผิดพลาด" ทั่วไป เพราะผู้ใช้ต้องรู้ว่าชนกฎข้อไหน
   */
  readonly rule: string | null;
  readonly details: Readonly<Record<string, unknown>>;
}

const EMPTY_ENVELOPE: ApiErrorEnvelope = {
  error_code: null,
  message: null,
  rule: null,
  details: {},
};

/**
 * ความล้มเหลวทุกแบบที่ contract นิยามไว้ แยกเป็นสถานะของตัวเอง
 *
 * ไม่ยุบ 403/404/409/422 เข้าด้วยกันโดยเจตนา: ผู้ใช้ต้องทำต่างกันในแต่ละกรณี
 * และการยุบทิ้งทำให้ "ไม่มีสิทธิ์" อ่านเหมือน "ไม่มีข้อมูล"
 */
export type ProductFailure =
  | { readonly status: 'unauthenticated' }
  | { readonly status: 'forbidden'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'not-found'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'conflict'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'rule-violation'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'bad-request'; readonly error: ApiErrorEnvelope }
  | { readonly status: 'server-error'; readonly httpStatus: number }
  | { readonly status: 'network-error'; readonly detail: string }
  | { readonly status: 'invalid-response' }
  | { readonly status: 'cancelled' };

export type ProductListResult =
  | { readonly status: 'ready'; readonly page: ProductListPage }
  | ProductFailure;

export type ProductResult =
  | { readonly status: 'ready'; readonly product: Product }
  | ProductFailure;

export interface ProductRequestOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ตัวเดียวกับที่ `lib/permissions/client.ts` ใช้ —
   * header `Authorization: Bearer` ถูกเติมที่นั่นจุดเดียว ไม่ใช่ที่ client แต่ละตัว
   * เพื่อไม่ให้มี client ตัวไหน (รวมถึงตัวที่ยังไม่ได้เขียน) ลืมส่ง token ได้เลย
   */
  readonly fetchImpl?: typeof fetch;
}

/* -------------------------------------------------------------- query ---- */

/**
 * ต่อ query string ตามลำดับที่ contract เขียนไว้
 *
 * ลำดับคงที่เพื่อให้ URL ที่เทสต์ยืนยันอ่านง่ายและ diff ได้ ค่าที่เป็นสตริงว่าง
 * ถูกตัดทิ้ง — `?q=` ไม่ได้แปลว่า "ค้นหาคำว่างเปล่า" แต่แปลว่าผู้ใช้ยังไม่ได้กรอก
 */
export function buildProductsQuery(filters: ProductListFilters = {}): string {
  const params = new URLSearchParams();
  const append = (key: string, value: string | undefined) => {
    if (typeof value === 'string' && value.trim() !== '') {
      params.append(key, value.trim());
    }
  };

  append('plant_code', filters.plant_code);
  append('product_type', filters.product_type);
  append('status', filters.status);
  append('model', filters.model);
  append('default_replenishment_mode', filters.default_replenishment_mode);
  append('q', filters.q);
  if (typeof filters.limit === 'number' && Number.isInteger(filters.limit)) {
    params.append('limit', String(filters.limit));
  }
  append('cursor', filters.cursor);

  const query = params.toString();
  return query === '' ? '' : `?${query}`;
}

/* -------------------------------------------------------------- parsing -- */

/** ค่าที่ต้องมีจริง: string ที่ไม่ว่าง อย่างอื่นถือว่า payload ผิดรูป */
function requiredString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * ฟิลด์ที่ nullable ได้ — `undefined` แปลว่า "ผิดรูป"
 *
 * แยก "ไม่มีค่า" (null) ออกจาก "ชนิดผิด" (undefined) โดยตั้งใจ ฟิลด์ที่หายไป
 * ถือเป็น null ได้ แต่ตัวเลขที่มาในช่องที่ต้องเป็นข้อความถือว่าผิดรูป
 */
function nullableString(value: unknown): string | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'string' ? value : undefined;
}

/**
 * ตัวเลข `NUMERIC` — รับเฉพาะ string เท่านั้น
 *
 * ถ้าเซิร์ฟเวอร์ส่ง `number` มา ความละเอียดอาจหายไปแล้วตั้งแต่ก่อนถึงที่นี่
 * (`NUMERIC(18,4)` ไม่รอดจาก IEEE-754 double) การ "ยอมรับไว้ก่อน" คือการ
 * กลืนข้อมูลที่เพี้ยนแล้วไปแสดงเป็นต้นทุน จึงถือเป็น payload ผิดรูปแทน
 */
function numericString(value: unknown): string | null | undefined {
  return nullableString(value);
}

function nullableInteger(value: unknown): number | null | undefined {
  if (value === null || value === undefined) {
    return null;
  }
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

export function parseProductPlantData(raw: unknown): ProductPlantData | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const plantId = requiredString(record.plant_id);
  const plantCode = requiredString(record.plant_code);
  const mode = requiredString(record.default_replenishment_mode);
  if (plantId === null || plantCode === null || mode === null) {
    return null;
  }

  const lotSizeProcedure = nullableString(record.lot_size_procedure);
  const plannedDeliveryTime = nullableInteger(record.planned_delivery_time);
  const safetyStock = numericString(record.safety_stock);
  const unitCost = numericString(record.unit_cost);
  const valuationClass = nullableString(record.valuation_class);
  if (
    lotSizeProcedure === undefined ||
    plannedDeliveryTime === undefined ||
    safetyStock === undefined ||
    unitCost === undefined ||
    valuationClass === undefined
  ) {
    return null;
  }

  return {
    plant_id: plantId,
    plant_code: plantCode,
    default_replenishment_mode: mode,
    lot_size_procedure: lotSizeProcedure,
    planned_delivery_time: plannedDeliveryTime,
    safety_stock: safetyStock,
    unit_cost: unitCost,
    valuation_class: valuationClass,
  };
}

export function parseProduct(raw: unknown): Product | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const productId = requiredString(record.product_id);
  const productCode = requiredString(record.product_code);
  const name = requiredString(record.name);
  const model = nullableString(record.model);
  const kgPerPiece = numericString(record.kg_per_piece);
  const materialSpec = nullableString(record.material_spec);
  const materialForm = nullableString(record.material_form);
  const thicknessMm = numericString(record.thickness_mm);
  const widthMm = numericString(record.width_mm);
  const lengthMm = numericString(record.length_mm);
  const productType = requiredString(record.product_type);
  const baseUom = requiredString(record.base_uom);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  if (
    productId === null ||
    productCode === null ||
    name === null ||
    productType === null ||
    baseUom === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const createdBy = nullableString(record.created_by_employee_id);
  const submittedBy = nullableString(record.submitted_by_employee_id);
  const submittedAt = nullableString(record.submitted_at);
  const approvedBy = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  if (
    model === undefined ||
    kgPerPiece === undefined ||
    materialSpec === undefined ||
    materialForm === undefined ||
    thicknessMm === undefined ||
    widthMm === undefined ||
    lengthMm === undefined ||
    createdBy === undefined ||
    submittedBy === undefined ||
    submittedAt === undefined ||
    approvedBy === undefined ||
    approvedAt === undefined
  ) {
    return null;
  }

  // `plant_data` ที่หายไปถือเป็น "ไม่มี Plant ที่คุณเห็น" ซึ่งเกิดขึ้นได้จริงเมื่อ
  // scope กรองจนหมด แต่ถ้ามาแล้วไม่ใช่ array หรือมีแถวผิดรูป = payload ผิดรูป
  const rawPlantData = record.plant_data;
  const plantData: ProductPlantData[] = [];
  if (rawPlantData !== undefined && rawPlantData !== null) {
    if (!Array.isArray(rawPlantData)) {
      return null;
    }
    for (const entry of rawPlantData) {
      const parsed = parseProductPlantData(entry);
      if (parsed === null) {
        return null;
      }
      plantData.push(parsed);
    }
  }

  return {
    product_id: productId,
    product_code: productCode,
    name,
    model,
    product_type: productType,
    base_uom: baseUom,
    kg_per_piece: kgPerPiece,
    material_spec: materialSpec,
    material_form: materialForm,
    thickness_mm: thicknessMm,
    width_mm: widthMm,
    length_mm: lengthMm,
    status,
    created_by_employee_id: createdBy,
    submitted_by_employee_id: submittedBy,
    submitted_at: submittedAt,
    approved_by_employee_id: approvedBy,
    approved_at: approvedAt,
    created_at: createdAt,
    updated_at: updatedAt,
    plant_data: plantData,
  };
}

export function parseProductListPage(raw: unknown): ProductListPage | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  if (!Array.isArray(record.items)) {
    return null;
  }
  const items: Product[] = [];
  for (const entry of record.items) {
    const parsed = parseProduct(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = nullableString(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }

  // cursor ที่เป็นสตริงว่างไม่ใช่ "มีหน้าถัดไป" — ปุ่มโหลดเพิ่มที่ส่ง cursor ว่าง
  // จะวนขอหน้าเดิมซ้ำไปเรื่อย ๆ
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  if (typeof raw !== 'object' || raw === null) {
    return EMPTY_ENVELOPE;
  }
  const record = raw as Record<string, unknown>;
  const details =
    typeof record.details === 'object' && record.details !== null
      ? (record.details as Record<string, unknown>)
      : {};
  const rule = details.rule;

  return {
    error_code: requiredString(record.error_code),
    message: requiredString(record.message),
    rule: requiredString(rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    // error body ที่อ่านไม่ออกไม่ควรกลบสถานะ HTTP ที่อ่านออกแล้ว
    return EMPTY_ENVELOPE;
  }
}

/** ใช้เฉพาะโดย `exportProducts` — `downloadFile()` คืน `Response` ดิบตอน `!ok` แทนการแปลให้ */
async function productExportFailureFromResponse(response: Response): Promise<ProductFailure | null> {
  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  return null;
}

type PayloadResult = { readonly status: 'payload'; readonly payload: unknown } | ProductFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: ProductRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

/** `GET /v1/products` — list ที่ถูกจำกัดด้วย plant scope ของผู้เรียกเสมอ */
export async function fetchProductList(
  filters: ProductListFilters = {},
  options: ProductRequestOptions = {},
): Promise<ProductListResult> {
  const result = await requestPayload(
    `${PRODUCTS_PATH}${buildProductsQuery(filters)}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseProductListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** `GET /v1/products/{product_id}` — รายละเอียด + plant_data ที่ผู้เรียกเห็น */
export async function fetchProduct(
  productId: string,
  options: ProductRequestOptions = {},
): Promise<ProductResult> {
  const result = await requestPayload(
    productPath(productId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const product = parseProduct(result.payload);
  return product === null ? { status: 'invalid-response' } : { status: 'ready', product };
}

/**
 * อ่าน `details.current_status` จากความล้มเหลวที่มี envelope
 *
 * `409` ของ transition ตอบสถานะจริงมาด้วยเสมอ และนั่นคือสิ่งเดียวที่บอกผู้ใช้ได้ว่า
 * หน้าจอที่เปิดค้างไว้ตกยุคไปเป็นอะไรแล้ว — คืน `null` เมื่อไม่มีค่าที่อ่านได้
 * แทนที่จะเดา เพราะการเดาสถานะของ record ที่คนอื่นเพิ่งขยับ คือความผิดพลาดชนิด
 * เดียวกับที่ guard นี้มีไว้ป้องกัน
 */
export function currentStatusFromFailure(failure: ProductFailure): string | null {
  if (!('error' in failure)) {
    return null;
  }
  const value = failure.error.details.current_status;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * แปลง body ให้เหลือเฉพาะคีย์ที่ contract รับ
 *
 * ด่านที่สองต่อจากชนิดข้อมูล: ต่อให้ object ที่ส่งเข้ามามีคีย์แปลกปลอมติดมา
 * (เช่น state ของฟอร์มที่ถูก spread ทั้งก้อน) สิ่งที่ออกไปกับ request ยังเป็น
 * เฉพาะคีย์ที่นี่เท่านั้น — `status` และฟิลด์ผู้กระทำทั้งห้าจึงไปถึงเซิร์ฟเวอร์
 * ไม่ได้เลย ซึ่งเป็นเงื่อนไขที่ทำให้ D-010 ยังมีความหมาย
 */
export function serializeCreateProductBody(body: CreateProductBody): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    product_code: body.product_code,
    name: body.name,
    product_type: body.product_type,
    base_uom: body.base_uom,
    plant_data: body.plant_data.map(serializeCreateProductPlantInput),
  };
  if (body.model !== undefined) {
    payload.model = body.model;
  }
  // ตัวเลขเป็น string ที่ผู้ใช้พิมพ์ ไม่ผ่าน Number (D-165: เซิร์ฟเวอร์ปฏิเสธ JSON float)
  for (const field of PRODUCT_STEEL_FIELDS) {
    if (body[field] !== undefined) {
      payload[field] = body[field];
    }
  }
  return payload;
}

function serializeCreateProductPlantInput(
  input: CreateProductPlantInput,
): Record<string, unknown> {
  const entry: Record<string, unknown> = { plant_code: input.plant_code };
  if (input.default_replenishment_mode !== undefined) {
    entry.default_replenishment_mode = input.default_replenishment_mode;
  }
  if (input.valuation_class !== undefined) {
    entry.valuation_class = input.valuation_class;
  }
  if (input.lot_size_procedure !== undefined) {
    entry.lot_size_procedure = input.lot_size_procedure;
  }
  if (input.planned_delivery_time !== undefined) {
    entry.planned_delivery_time = input.planned_delivery_time;
  }
  if (input.safety_stock !== undefined) {
    entry.safety_stock = input.safety_stock;
  }
  if (input.unit_cost !== undefined) {
    entry.unit_cost = input.unit_cost;
  }
  return entry;
}

/**
 * `POST /v1/products` — คืน `201` พร้อม resource เต็ม
 *
 * ไม่ส่ง `Idempotency-Key`: contract กำหนดว่า header นั้นสงวนไว้ให้ Scan,
 * Goods Receipt, Initial Import และ Offline Sync ที่กระทบ stock/accounting
 * การใส่กับ Product Master จะทำให้ความหมายของ `request_dedup` เบลอ
 */
export async function createProduct(
  body: CreateProductBody,
  options: ProductRequestOptions = {},
): Promise<ProductResult> {
  const result = await requestPayload(
    PRODUCTS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateProductBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const product = parseProduct(result.payload);
  return product === null ? { status: 'invalid-response' } : { status: 'ready', product };
}

/**
 * แปลง body ของการแก้ไขให้เหลือเฉพาะสี่คีย์ที่ `PATCH` รับ
 *
 * ด่านเดียวกับ `serializeCreateProductBody()` แต่มีข้อที่ต่างออกไปหนึ่งข้อ:
 * ที่นี่ **การไม่มีคีย์กับการมีคีย์ที่เป็น `null` ไม่เหมือนกัน** เซิร์ฟเวอร์อ่าน
 * "ไม่มีคีย์" ว่าไม่ต้องแตะฟิลด์นั้น และอ่าน `null` ว่าให้ล้างค่า ฟังก์ชันนี้จึง
 * ตัดทิ้งเฉพาะ `undefined` และส่ง `null` ต่อไปตามที่ผู้เรียกตั้งใจ
 *
 * สิ่งที่ออกไปไม่ได้เลยด้วยโครงสร้างนี้: `product_code` (รหัสธุรกิจเปลี่ยนไม่ได้),
 * `status`, ฟิลด์ผู้กระทำทั้งห้า และ `expected_status` — ตัวสุดท้ายเพราะ `PATCH`
 * บน `DRAFT` เป็น last-write-wins ตาม contract ไม่ใช่เพราะมองข้ามไป
 */
export function serializeUpdateProductBody(body: UpdateProductBody): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (body.name !== undefined) {
    payload.name = body.name;
  }
  // `undefined` ตัดทิ้ง (= "ไม่แตะ") แต่ `null` ต้องอยู่ใน JSON จริง (= "ล้างค่า") —
  // เช็คแยกจากฟิลด์อื่นเพราะ `null` เป็นค่าที่ต้องส่งออกไป ไม่ใช่ค่าที่ตัดทิ้ง
  if (body.model !== undefined) {
    payload.model = body.model;
  }
  if (body.product_type !== undefined) {
    payload.product_type = body.product_type;
  }
  if (body.base_uom !== undefined) {
    payload.base_uom = body.base_uom;
  }
  // `null` ล้างค่า, `undefined` ไม่แตะ — เหมือน `model`
  for (const field of PRODUCT_STEEL_FIELDS) {
    if (body[field] !== undefined) {
      payload[field] = body[field];
    }
  }
  if (body.plant_data !== undefined) {
    payload.plant_data = body.plant_data.map(serializeUpdateProductPlantInput);
  }
  return payload;
}

function serializeUpdateProductPlantInput(
  input: UpdateProductPlantInput,
): Record<string, unknown> {
  const entry: Record<string, unknown> = { plant_code: input.plant_code };
  if (input.default_replenishment_mode !== undefined) {
    entry.default_replenishment_mode = input.default_replenishment_mode;
  }
  if (input.valuation_class !== undefined) {
    entry.valuation_class = input.valuation_class;
  }
  if (input.lot_size_procedure !== undefined) {
    entry.lot_size_procedure = input.lot_size_procedure;
  }
  if (input.planned_delivery_time !== undefined) {
    entry.planned_delivery_time = input.planned_delivery_time;
  }
  if (input.safety_stock !== undefined) {
    entry.safety_stock = input.safety_stock;
  }
  if (input.unit_cost !== undefined) {
    entry.unit_cost = input.unit_cost;
  }
  return entry;
}

/**
 * `PATCH /v1/products/{product_id}` — คืน resource เต็มที่เซิร์ฟเวอร์บันทึกจริง
 *
 * **ไม่มี `If-Match` และไม่มี `expected_status`** contract ระบุว่า `PATCH` บน
 * `DRAFT` เป็น last-write-wins โดยการตัดสินใจ การเพิ่ม header หรือคีย์ที่
 * เซิร์ฟเวอร์ไม่ได้อ่านเข้ามา จะได้หน้าจอที่ดูเหมือนมีด่านกันการเขียนทับ ทั้งที่
 * ไม่มีอะไรกันเลย ซึ่งอันตรายกว่าการไม่มีด่านแล้วบอกตามตรง
 *
 * เช่นเดียวกับ `createProduct()` — ไม่ส่ง `Idempotency-Key` เพราะ contract สงวน
 * header นั้นไว้ให้ธุรกรรมที่กระทบ stock/accounting
 */
export async function updateProduct(
  productId: string,
  body: UpdateProductBody,
  options: ProductRequestOptions = {},
): Promise<ProductResult> {
  const result = await requestPayload(
    productPath(productId),
    {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeUpdateProductBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const product = parseProduct(result.payload);
  return product === null ? { status: 'invalid-response' } : { status: 'ready', product };
}

/**
 * body ของ transition — คีย์เดียวเท่านั้น
 *
 * เหตุผลเดียวกับ `serializeCreateProductBody()` แต่เข้มกว่า: สิ่งที่ออกไปกับ
 * request คือ `expected_status` ตัวเดียว ต่อให้ผู้เรียกถือ object อื่นอยู่ในมือ
 * `status` และฟิลด์ผู้กระทำจึงไปถึงเซิร์ฟเวอร์ไม่ได้เลย (contract ตอบ `400`
 * ให้ request ที่เอ่ยถึงฟิลด์เหล่านั้น และ D-010 จะไร้ความหมายถ้า client
 * ระบุผู้อนุมัติเองได้)
 */
export function serializeProductTransitionBody(expectedStatus: string): ProductTransitionBody {
  return { expected_status: expectedStatus };
}

/**
 * `POST /v1/products/{product_id}/{submit|approve|reject}` — คืน resource เต็ม
 *
 * `expectedStatus` เป็น parameter บังคับโดยเจตนา ไม่ใช่ค่าที่ฟังก์ชันนี้ไปอ่านเอง
 * จาก record: ผู้เรียกต้องส่ง **สถานะที่ผู้ใช้เห็นอยู่บนจอ** มาเอง ถ้าปล่อยให้
 * client ไปหยิบสถานะล่าสุดมาใส่ให้เอง guard ก็จะยืนยันได้แค่ว่า "ข้อมูลที่เพิ่ง
 * โหลดมาตรงกับข้อมูลที่เพิ่งโหลดมา" ซึ่งไม่กันอะไรเลย
 *
 * เช่นเดียวกับ `createProduct()` — ไม่ส่ง `Idempotency-Key` เพราะ contract สงวน
 * header นั้นไว้ให้ธุรกรรมที่กระทบ stock/accounting
 */
export async function transitionProduct(
  productId: string,
  transition: ProductTransition,
  expectedStatus: string,
  options: ProductRequestOptions = {},
): Promise<ProductResult> {
  const result = await requestPayload(
    productTransitionPath(productId, transition),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeProductTransitionBody(expectedStatus)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const product = parseProduct(result.payload);
  return product === null ? { status: 'invalid-response' } : { status: 'ready', product };
}

/* ------------------------------------------------- change requests ------- */

/**
 * `.../{product_id}/change-requests` — เส้นทางเดียวของทั้ง list และ create
 *
 * ใช้ `product_id` ด้วยเหตุผลเดียวกับ `productPath()`: รหัสธุรกิจเปลี่ยนได้
 */
export function productChangeRequestsPath(productId: string): string {
  return `${productPath(productId)}/change-requests`;
}

/**
 * `.../change-requests/{cr_id}/{approve|reject}`
 *
 * ชื่อคำตัดสินมาจาก `PRODUCT_CHANGE_REQUEST_DECISIONS` ซึ่งเป็น path segment จริง
 * ชนิดข้อมูลจึงเป็นตัวกันไม่ให้มีคำตัดสินที่สามโผล่มาจากสตริงที่พิมพ์เอง
 */
export function productChangeRequestDecisionPath(
  productId: string,
  changeRequestId: string,
  decision: ProductChangeRequestDecision,
): string {
  return `${productChangeRequestsPath(productId)}/${encodeURIComponent(
    changeRequestId,
  )}/${decision}`;
}

export type ProductChangeRequestResult =
  | { readonly status: 'ready'; readonly changeRequest: ProductChangeRequest }
  | ProductFailure;

export type ProductChangeRequestListResult =
  | { readonly status: 'ready'; readonly page: ProductChangeRequestListPage }
  | ProductFailure;

/**
 * map ที่เปิดอยู่ (`proposed_changes` / `baseline`)
 *
 * คีย์ที่หายไปทั้งก้อนถือเป็น map ว่าง เพราะ `DEACTIVATE` มี `proposed_changes`
 * ว่างโดยกติกา แต่ array หรือค่า scalar ถือว่า payload ผิดรูป — ข้อเสนอที่อ่าน
 * ไม่ออกต้องไม่ถูกแสดงเป็น "ข้อเสนอที่ไม่แก้อะไรเลย"
 */
function changeMap(value: unknown): Readonly<Record<string, unknown>> | null {
  if (value === undefined || value === null) {
    return {};
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function parseProductChangeRequest(raw: unknown): ProductChangeRequest | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const changeRequestId = requiredString(record.change_request_id);
  const productId = requiredString(record.product_id);
  const changeType = requiredString(record.change_type);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  if (
    changeRequestId === null ||
    productId === null ||
    changeType === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  const plantCode = nullableString(record.plant_code);
  const createdBy = nullableString(record.created_by_employee_id);
  const approvedBy = nullableString(record.approved_by_employee_id);
  const approvedAt = nullableString(record.approved_at);
  if (
    plantCode === undefined ||
    createdBy === undefined ||
    approvedBy === undefined ||
    approvedAt === undefined
  ) {
    return null;
  }

  const proposedChanges = changeMap(record.proposed_changes);
  const baseline = changeMap(record.baseline);
  if (proposedChanges === null || baseline === null) {
    return null;
  }

  return {
    change_request_id: changeRequestId,
    product_id: productId,
    plant_code: plantCode,
    change_type: changeType,
    proposed_changes: proposedChanges,
    baseline,
    status,
    created_by_employee_id: createdBy,
    approved_by_employee_id: approvedBy,
    approved_at: approvedAt,
    created_at: createdAt,
    updated_at: updatedAt,
  };
}

/**
 * อ่านรายการข้อเสนอได้ทั้งสอง envelope ที่ contract เปิดทางไว้
 *
 * ⚠️ contract freeze รูปร่างของ *resource* ไว้ครบ แต่หัวข้อ `GET` บอกแค่ว่าคืน
 * "รายการข้อเสนอของ product นั้น" — จะเป็น array เปล่า ๆ หรือ envelope
 * `{ items, next_cursor }` แบบ `GET /v1/products` ก็อ่านได้ทั้งคู่ ที่นี่จึงรับ
 * ทั้งสองแบบแทนการเลือกข้างให้ backend ซึ่งกำลังถูกสร้างขนานกันจากเอกสารฉบับ
 * เดียวกัน การเลือกข้างเงียบ ๆ แล้วผิด จะกลายเป็นหน้าจอที่ขึ้น "ผิดรูป" ทั้งที่
 * เซิร์ฟเวอร์ตอบถูกตามที่ตัวเองอ่านเอกสาร — ประเด็นนี้ถูกรายงานเป็น blocker แล้ว
 */
export function parseProductChangeRequestList(
  raw: unknown,
): ProductChangeRequestListPage | null {
  const rawItems = Array.isArray(raw)
    ? raw
    : typeof raw === 'object' && raw !== null
      ? (raw as Record<string, unknown>).items
      : undefined;
  if (!Array.isArray(rawItems)) {
    return null;
  }

  const items: ProductChangeRequest[] = [];
  for (const entry of rawItems) {
    const parsed = parseProductChangeRequest(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  if (Array.isArray(raw)) {
    return { items, next_cursor: null };
  }

  const cursor = nullableString((raw as Record<string, unknown>).next_cursor);
  if (cursor === undefined) {
    return null;
  }
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

/**
 * แปลง body ให้เหลือเฉพาะสามคีย์ที่ contract รับ
 *
 * ด่านเดียวกับ `serializeCreateProductBody()` และมีข้อที่สำคัญกว่าหนึ่งข้อ:
 * **`baseline` ออกไปไม่ได้เลย** ต่อให้ผู้เรียกถือ object ที่มีคีย์นั้นอยู่ในมือ
 * เพราะ baseline ที่ client เขียนเองจะทำให้การเทียบค่าตอน approve (และ
 * `409 stale_fields` ที่ตามมา) กลายเป็นการเทียบกับความเห็นของผู้เสนอ
 * `plant_code` ถูกส่งเสมอ รวมทั้งตอนเป็น `null` เพราะ `null` ที่นี่แปลว่า
 * "ระดับ product" ซึ่งเป็นข้อมูล ไม่ใช่การละเว้น
 */
export function serializeCreateProductChangeRequestBody(
  body: CreateProductChangeRequestBody,
): Record<string, unknown> {
  return {
    change_type: body.change_type,
    plant_code: body.plant_code,
    proposed_changes: { ...body.proposed_changes },
  };
}

/** body ของ approve/reject — คีย์เดียว ด้วยเหตุผลเดียวกับ transition ของ product */
export function serializeProductChangeRequestDecisionBody(
  expectedStatus: string,
): ProductChangeRequestDecisionBody {
  return { expected_status: expectedStatus };
}

/** `GET /v1/products/{product_id}/change-requests` */
export async function fetchProductChangeRequests(
  productId: string,
  options: ProductRequestOptions = {},
): Promise<ProductChangeRequestListResult> {
  const result = await requestPayload(
    productChangeRequestsPath(productId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseProductChangeRequestList(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/**
 * `POST /v1/products/{product_id}/change-requests`
 *
 * ไม่ส่ง `Idempotency-Key` ด้วยเหตุผลเดียวกับทั้งกลุ่ม: contract สงวน header นั้น
 * ไว้ให้ธุรกรรมที่กระทบ stock/accounting
 */
export async function createProductChangeRequest(
  productId: string,
  body: CreateProductChangeRequestBody,
  options: ProductRequestOptions = {},
): Promise<ProductChangeRequestResult> {
  const result = await requestPayload(
    productChangeRequestsPath(productId),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateProductChangeRequestBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const changeRequest = parseProductChangeRequest(result.payload);
  return changeRequest === null
    ? { status: 'invalid-response' }
    : { status: 'ready', changeRequest };
}

/**
 * `POST /v1/products/{product_id}/change-requests/{cr_id}/{approve|reject}`
 *
 * `expectedStatus` เป็น parameter บังคับด้วยเหตุผลเดียวกับ `transitionProduct()`:
 * ค่าที่ส่งต้องเป็นสถานะของข้อเสนอ **ที่ผู้ใช้เห็นอยู่บนจอ** ถ้าฟังก์ชันนี้ไปอ่าน
 * สถานะล่าสุดมาใส่เอง guard ก็จะยืนยันได้แค่ว่าข้อมูลที่เพิ่งโหลดตรงกับข้อมูลที่
 * เพิ่งโหลด ซึ่งไม่กันอะไรเลย
 */
export async function decideProductChangeRequest(
  productId: string,
  changeRequestId: string,
  decision: ProductChangeRequestDecision,
  expectedStatus: string,
  options: ProductRequestOptions = {},
): Promise<ProductChangeRequestResult> {
  const result = await requestPayload(
    productChangeRequestDecisionPath(productId, changeRequestId, decision),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeProductChangeRequestDecisionBody(expectedStatus)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const changeRequest = parseProductChangeRequest(result.payload);
  return changeRequest === null
    ? { status: 'invalid-response' }
    : { status: 'ready', changeRequest };
}

/**
 * ชื่อฟิลด์ที่ `409` บอกว่า "มีคนอื่นแก้ไปแล้วหลังข้อเสนอนี้ถูกเขียน"
 *
 * นี่คือหัวใจของ D-042 ทั้งข้อ: ข้อเสนอที่ค้างไว้หลายวันแล้วถูก approve ทับงาน
 * ของคนอื่นเงียบ ๆ คืออุบัติเหตุที่ตารางนี้มีไว้ป้องกันพอดี หน้าจอจึงต้องบอกได้ว่า
 * **ฟิลด์ไหน** ไม่ใช่แค่ "ชนกัน"
 *
 * รับทั้ง array ของชื่อฟิลด์ และ object ที่ map ชื่อฟิลด์ไปหาค่าปัจจุบัน —
 * contract เขียนแค่ `details.stale_fields` โดยไม่ได้ freeze รูปร่างข้างใน
 * (รายงานเป็น blocker แล้ว) คืนรายการว่างเมื่ออ่านไม่ได้ ไม่ใช่เดาชื่อฟิลด์ขึ้นมาเอง
 */
export function staleFieldsFromFailure(failure: ProductFailure): readonly string[] {
  if (!('error' in failure)) {
    return [];
  }
  const value = failure.error.details.stale_fields;
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => typeof entry === 'string' && entry !== '');
  }
  if (typeof value === 'object' && value !== null) {
    return Object.keys(value as Record<string, unknown>);
  }
  return [];
}

/**
 * สิ่งที่ยังอ้างถึง product อยู่ ตอนขออนุมัติ `DEACTIVATE`
 *
 * `references/mrp-core-and-accounting.md` ระบุว่าต้อง **เตือน** ไม่ใช่ปิดกั้นเงียบ ๆ
 * คำตอบนี้จึงเป็นระบบกำลังบอกผู้ใช้ว่าต้องไปจัดการอะไรต่อ การยุบเป็น
 * "ทำรายการไม่สำเร็จ" คือการทิ้งคำตอบนั้นไป
 *
 * แต่ละรายการถูกแปลงเป็นข้อความบรรทัดเดียวโดยไม่ตีความ: สตริงใช้ตามที่ได้มา
 * ส่วน object ถูกกางเป็น `key: value` ทุกคู่ที่แสดงได้ เพื่อให้ field ที่หน้าจอนี้
 * ยังไม่รู้จักก็ยังไปถึงสายตาผู้ใช้
 */
export function openReferencesFromFailure(failure: ProductFailure): readonly string[] {
  if (!('error' in failure)) {
    return [];
  }
  const value = failure.error.details.open_references;
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map((entry) => {
      if (typeof entry === 'string') {
        return entry;
      }
      if (typeof entry === 'number' || typeof entry === 'boolean') {
        return String(entry);
      }
      if (typeof entry === 'object' && entry !== null) {
        return Object.entries(entry as Record<string, unknown>)
          .map(([key, item]) => `${key}: ${formatChangeValue(item)}`)
          .join(' · ');
      }
      return '';
    })
    .filter((entry) => entry !== '');
}

/**
 * ค่าหนึ่งค่าใน `proposed_changes`/`baseline`/`open_references` ที่แสดงได้เสมอ
 *
 * ⚠️ ตัวเลข `NUMERIC` มาเป็น **string** และต้องแสดงเป็น string ตัวเดิมทุกตัวอักษร
 * (`String()` กับ string คือ identity) — ไม่มีเส้นทางไหนในนี้ที่ค่าจะถูกยัดผ่าน
 * `Number` ซึ่งจะกินทศนิยมของ `NUMERIC(18,4)` หายไปเงียบ ๆ
 *
 * `null` แสดงเป็น `null` ตรง ๆ ไม่ใช่ช่องว่าง เพราะในบริบทนี้ `null` มีความหมาย
 * ("ล้างค่า") ที่ต่างจาก "ไม่ได้เสนอฟิลด์นี้" อย่างสิ้นเชิง
 */
export function formatChangeValue(value: unknown): string {
  if (value === null) {
    return 'null';
  }
  if (value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

/* --------------------------------------------- product type register ---- */

/** เส้นทางจริงของทะเบียนประเภทสินค้า — `router.py` บรรทัด ~1605-1922 */
export const PRODUCT_TYPES_PATH = '/v1/product-types';

export function productTypePath(typeCode: string): string {
  return `${PRODUCT_TYPES_PATH}/${encodeURIComponent(typeCode)}`;
}

export function productTypeDeactivatePath(typeCode: string): string {
  return `${productTypePath(typeCode)}/deactivate`;
}

export function productTypeActivatePath(typeCode: string): string {
  return `${productTypePath(typeCode)}/activate`;
}

export function parseProductTypeEntry(raw: unknown): ProductTypeEntry | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const typeCode = requiredString(record.type_code);
  const name = requiredString(record.name);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  if (
    typeCode === null ||
    name === null ||
    status === null ||
    createdAt === null ||
    updatedAt === null
  ) {
    return null;
  }

  return { type_code: typeCode, name, status, created_at: createdAt, updated_at: updatedAt };
}

export function parseProductTypeListPage(raw: unknown): ProductTypeListPage | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  if (!Array.isArray(record.items)) {
    return null;
  }
  const items: ProductTypeEntry[] = [];
  for (const entry of record.items) {
    const parsed = parseProductTypeEntry(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = nullableString(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

export type ProductTypeListResult =
  | { readonly status: 'ready'; readonly page: ProductTypeListPage }
  | ProductFailure;

export type ProductTypeResult =
  | { readonly status: 'ready'; readonly entry: ProductTypeEntry }
  | ProductFailure;

/** `GET /v1/product-types` — `status` ทางเลือก (แบบฟอร์มสร้างสินค้าใช้ `ACTIVE`) */
export async function fetchProductTypes(
  filters: { readonly status?: string } = {},
  options: ProductRequestOptions = {},
): Promise<ProductTypeListResult> {
  const params = new URLSearchParams();
  if (typeof filters.status === 'string' && filters.status.trim() !== '') {
    params.append('status', filters.status.trim());
  }
  const query = params.toString();

  const result = await requestPayload(
    `${PRODUCT_TYPES_PATH}${query === '' ? '' : `?${query}`}`,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseProductTypeListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** คีย์เดียวที่ `POST /v1/product-types` รับ — `status` เป็นของเซิร์ฟเวอร์เสมอ */
export function serializeCreateProductTypeBody(
  body: CreateProductTypeBody,
): Record<string, unknown> {
  return { type_code: body.type_code, name: body.name };
}

/** `POST /v1/product-types` — ต้องมีสิทธิ์ `EDIT` (D-115 ข้อ 4: เพิ่ม/แก้ชื่อเป็นของผู้ดูแลข้อมูล) */
export async function createProductType(
  body: CreateProductTypeBody,
  options: ProductRequestOptions = {},
): Promise<ProductTypeResult> {
  const result = await requestPayload(
    PRODUCT_TYPES_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateProductTypeBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const entry = parseProductTypeEntry(result.payload);
  return entry === null ? { status: 'invalid-response' } : { status: 'ready', entry };
}

/** ตัดทิ้งทุกคีย์ยกเว้น `name` — `type_code` แก้ไม่ได้และ `status` ขยับผ่าน transition เท่านั้น */
export function serializeUpdateProductTypeBody(
  body: UpdateProductTypeBody,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (body.name !== undefined) {
    payload.name = body.name;
  }
  return payload;
}

/** `PATCH /v1/product-types/{type_code}` — ต้องมีสิทธิ์ `EDIT` */
export async function renameProductType(
  typeCode: string,
  body: UpdateProductTypeBody,
  options: ProductRequestOptions = {},
): Promise<ProductTypeResult> {
  const result = await requestPayload(
    productTypePath(typeCode),
    {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeUpdateProductTypeBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const entry = parseProductTypeEntry(result.payload);
  return entry === null ? { status: 'invalid-response' } : { status: 'ready', entry };
}

export function serializeProductTypeTransitionBody(
  expectedStatus: string,
): ProductTypeTransitionBody {
  return { expected_status: expectedStatus };
}

/**
 * `POST /v1/product-types/{type_code}/deactivate` — ต้องมีสิทธิ์ `APPROVE`
 * (D-115 ข้อ 4: ยกเลิกใช้งานเป็นการตัดสินใจระดับผู้บริหาร ต่างจากเพิ่ม/แก้ชื่อ)
 */
export async function deactivateProductType(
  typeCode: string,
  expectedStatus: string,
  options: ProductRequestOptions = {},
): Promise<ProductTypeResult> {
  const result = await requestPayload(
    productTypeDeactivatePath(typeCode),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeProductTypeTransitionBody(expectedStatus)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const entry = parseProductTypeEntry(result.payload);
  return entry === null ? { status: 'invalid-response' } : { status: 'ready', entry };
}

/** `POST /v1/product-types/{type_code}/activate` — ต้องมีสิทธิ์ `APPROVE` เหมือนกัน */
export async function activateProductType(
  typeCode: string,
  expectedStatus: string,
  options: ProductRequestOptions = {},
): Promise<ProductTypeResult> {
  const result = await requestPayload(
    productTypeActivatePath(typeCode),
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeProductTypeTransitionBody(expectedStatus)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const entry = parseProductTypeEntry(result.payload);
  return entry === null ? { status: 'invalid-response' } : { status: 'ready', entry };
}

/* ------------------------------------------------- unit of measure (uom) -- */

/** เส้นทางจริงของทะเบียนหน่วยนับ — `router.py` บรรทัด ~1924-2010 */
export const UOMS_PATH = '/v1/uoms';

export function uomPath(uomCode: string): string {
  return `${UOMS_PATH}/${encodeURIComponent(uomCode)}`;
}

export function parseUomEntry(raw: unknown): UomEntry | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const uomCode = requiredString(record.uom_code);
  const name = requiredString(record.name);
  const createdAt = requiredString(record.created_at);
  const updatedAt = requiredString(record.updated_at);
  if (uomCode === null || name === null || createdAt === null || updatedAt === null) {
    return null;
  }

  return { uom_code: uomCode, name, created_at: createdAt, updated_at: updatedAt };
}

export function parseUomListPage(raw: unknown): UomListPage | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  if (!Array.isArray(record.items)) {
    return null;
  }
  const items: UomEntry[] = [];
  for (const entry of record.items) {
    const parsed = parseUomEntry(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  const cursor = nullableString(record.next_cursor);
  if (cursor === undefined) {
    return null;
  }
  return { items, next_cursor: cursor === null || cursor.trim() === '' ? null : cursor };
}

export type UomListResult =
  | { readonly status: 'ready'; readonly page: UomListPage }
  | ProductFailure;

export type UomResult =
  | { readonly status: 'ready'; readonly entry: UomEntry }
  | ProductFailure;

/** `GET /v1/uoms` — ไม่มีตัวกรองสถานะ เพราะตารางไม่มีคอลัมน์นั้น */
export async function fetchUoms(options: ProductRequestOptions = {}): Promise<UomListResult> {
  const result = await requestPayload(
    UOMS_PATH,
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseUomListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

export function serializeCreateUomBody(body: CreateUomBody): Record<string, unknown> {
  return { uom_code: body.uom_code, name: body.name };
}

/** `POST /v1/uoms` — ต้องมีสิทธิ์ `EDIT` */
export async function createUom(
  body: CreateUomBody,
  options: ProductRequestOptions = {},
): Promise<UomResult> {
  const result = await requestPayload(
    UOMS_PATH,
    {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeCreateUomBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const entry = parseUomEntry(result.payload);
  return entry === null ? { status: 'invalid-response' } : { status: 'ready', entry };
}

/** ตัดทิ้งทุกคีย์ยกเว้น `name` — `uom_code` แก้ไม่ได้และไม่มีคอลัมน์สถานะให้แตะ */
export function serializeUpdateUomBody(body: UpdateUomBody): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (body.name !== undefined) {
    payload.name = body.name;
  }
  return payload;
}

/** `PATCH /v1/uoms/{uom_code}` — ต้องมีสิทธิ์ `EDIT`; ไม่มี deactivate/activate เลยในกลุ่มนี้ */
export async function renameUom(
  uomCode: string,
  body: UpdateUomBody,
  options: ProductRequestOptions = {},
): Promise<UomResult> {
  const result = await requestPayload(
    uomPath(uomCode),
    {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify(serializeUpdateUomBody(body)),
    },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const entry = parseUomEntry(result.payload);
  return entry === null ? { status: 'invalid-response' } : { status: 'ready', entry };
}

/* --------------------------------- D-142 — Export เป็น .xlsx (เมนู "ตัวเลือก") --- */

/** `POST /v1/products/export` — ไม่เขียนอะไร (`WO-P2-M11-022`, FREEZE) */
export const PRODUCTS_EXPORT_PATH = `${PRODUCTS_PATH}/export`;

/** ชื่อไฟล์ที่ผู้ใช้เห็นตอนเซฟ — ค่าเดียวกับฝั่งเซิร์ฟเวอร์ */
export const PRODUCTS_EXPORT_FILENAME = 'products.xlsx';

/** ตัวกรองของ Export เป๊ะเดียวกับรายการ ลบแค่ `limit`/`cursor` (D-118 ข้อ 5) */
export type ProductExportFilters = Omit<ProductListFilters, 'limit' | 'cursor'>;

/** ตัดช่องว่างหัวท้าย ค่าว่างทิ้งทั้งคีย์ — ว่างทั้งหมด = `{ filters: {} }` = ทุกแถวที่เห็น */
export function serializeProductExportRequest(
  filters: ProductExportFilters,
): Record<string, unknown> {
  const serialized: Record<string, string> = {};
  for (const [key, raw] of [
    ['plant_code', filters.plant_code],
    ['product_type', filters.product_type],
    ['status', filters.status],
    ['model', filters.model],
    ['default_replenishment_mode', filters.default_replenishment_mode],
    ['q', filters.q],
  ] as const) {
    const value = raw?.trim() ?? '';
    if (value !== '') {
      serialized[key] = value;
    }
  }
  return { filters: serialized };
}

export type ExportProductsResult = { readonly status: 'ready' } | ProductFailure;

/**
 * ขอ `.xlsx` ของสินค้าที่ตรงตัวกรองที่ใช้อยู่ แล้วให้เบราว์เซอร์เซฟ — ไม่มีอะไรถูกเขียน
 *
 * ยืม `downloadFile()` เหมือน `exportSalesOrders` ทั้งขั้นบันทึกไฟล์ โดยสวม `fetchImpl`
 * ให้เป็น `POST` พร้อม body — กลไก blob → anchor → revoke อยู่ที่ `lib/http/fileTransfer.ts`
 * ที่เดียว `422 PRODUCT_EXPORT_TOO_MANY_ROWS` มาถึงหน้าจอเป็น `rule-violation` พร้อม
 * ข้อความของเซิร์ฟเวอร์ ไม่ใช่ "ดาวน์โหลดพัง" เงียบ ๆ
 */
export async function exportProducts(
  filters: ProductExportFilters,
  options: ProductRequestOptions = {},
): Promise<ExportProductsResult> {
  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, PRODUCTS_EXPORT_PATH);
  const body = JSON.stringify(serializeProductExportRequest(filters));
  const fetchImpl = options.fetchImpl ?? apiFetch;

  const downloaded = await downloadFile(url, PRODUCTS_EXPORT_FILENAME, {
    fetchImpl: ((input: RequestInfo | URL, init?: RequestInit) =>
      fetchImpl(input, {
        ...init,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      })) as typeof fetch,
    ...(options.signal ? { signal: options.signal } : {}),
  });

  switch (downloaded.status) {
    case 'ready':
      return { status: 'ready' };
    case 'cancelled':
      return { status: 'cancelled' };
    case 'network-error':
      return { status: 'network-error', detail: downloaded.detail };
    case 'failed': {
      const failure = await productExportFailureFromResponse(downloaded.response);
      return failure ?? { status: 'server-error', httpStatus: downloaded.response.status };
    }
  }
}

/* --------------------------------- รูปสินค้า (D-143 ส่วนที่ 1, WO-P2-M11-025 FREEZE) --- */

export function productImagesPath(productId: string): string {
  return `${productPath(productId)}/images`;
}

export function productImagePath(productId: string, imageId: string): string {
  return `${productImagesPath(productId)}/${encodeURIComponent(imageId)}`;
}

export function productImageContentPath(productId: string, imageId: string): string {
  return `${productImagePath(productId, imageId)}/content`;
}

export function productImagePrimaryPath(productId: string, imageId: string): string {
  return `${productImagePath(productId, imageId)}/primary`;
}

export function productImageRemovePath(productId: string, imageId: string): string {
  return `${productImagePath(productId, imageId)}/remove`;
}

export function parseProductImage(raw: unknown): ProductImage | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const imageId = requiredString(record.image_id);
  const productId = requiredString(record.product_id);
  const filename = requiredString(record.filename);
  const contentType = requiredString(record.content_type);
  const status = requiredString(record.status);
  const createdAt = requiredString(record.created_at);
  const sizeBytes = record.size_bytes;
  const isPrimary = record.is_primary;
  if (
    imageId === null ||
    productId === null ||
    filename === null ||
    contentType === null ||
    status === null ||
    createdAt === null ||
    typeof sizeBytes !== 'number' ||
    typeof isPrimary !== 'boolean'
  ) {
    return null;
  }

  const caption = nullableString(record.caption);
  const createdBy = nullableString(record.created_by_employee_id);
  if (caption === undefined || createdBy === undefined) {
    return null;
  }

  return {
    image_id: imageId,
    product_id: productId,
    filename,
    content_type: contentType,
    size_bytes: sizeBytes,
    caption,
    is_primary: isPrimary,
    status,
    created_by_employee_id: createdBy,
    created_at: createdAt,
  };
}

export function parseProductImageListPage(raw: unknown): ProductImageListPage | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  if (!Array.isArray(record.items)) {
    return null;
  }
  const items: ProductImage[] = [];
  for (const entry of record.items) {
    const parsed = parseProductImage(entry);
    if (parsed === null) {
      return null;
    }
    items.push(parsed);
  }

  return { items };
}

export type ProductImageResult =
  | { readonly status: 'ready'; readonly image: ProductImage }
  | ProductFailure;

export type ProductImageListResult =
  | { readonly status: 'ready'; readonly page: ProductImageListPage }
  | ProductFailure;

export type ProductImageBlobResult =
  | { readonly status: 'ready'; readonly blob: Blob; readonly contentType: string }
  | ProductFailure;

/** `GET /v1/products/{product_id}/images` — ACTIVE เรียงรูปหลักก่อน ไม่มี cursor */
export async function listProductImages(
  productId: string,
  options: ProductRequestOptions = {},
): Promise<ProductImageListResult> {
  const result = await requestPayload(
    productImagesPath(productId),
    { method: 'GET', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const page = parseProductImageListPage(result.payload);
  return page === null ? { status: 'invalid-response' } : { status: 'ready', page };
}

/** ใช้เฉพาะโดย `uploadProductImage` — `uploadFile()` คืน `Response` ดิบ ต้องแปลเองเหมือน `exportProducts` */
async function productImageFailureFromResponse(response: Response): Promise<ProductFailure | null> {
  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    // 413 (ไฟล์เกินเพดาน) ไม่มีสถานะของตัวเองใน ProductFailure — ยุบเข้า rule-violation
    // เดียวกับ 422 เพราะ error_code/rule ของ envelope คือสิ่งที่ผู้เรียกใช้แยกกรณีจริง
    case 413:
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  return null;
}

/**
 * `POST /v1/products/{product_id}/images?filename=&caption=` — อัปโหลดรูปสินค้า
 *
 * ส่งไบต์ของรูปตรง ๆ ผ่าน `uploadFile()` เหมือน `importSalesOrderFromSpreadsheet` —
 * `filename`/`caption` ไปทาง query string ด้วย `encodeURIComponent` เอง (ไม่ใช้
 * `URLSearchParams` เพราะมันเข้ารหัสช่องว่างเป็น `+` ไม่ใช่ `%20` ตามสัญญา)
 */
export async function uploadProductImage(
  productId: string,
  blob: Blob,
  uploadOptions: UploadProductImageOptions,
  options: ProductRequestOptions = {},
): Promise<ProductImageResult> {
  const params = [`filename=${encodeURIComponent(uploadOptions.filename)}`];
  if (uploadOptions.caption !== undefined && uploadOptions.caption.trim() !== '') {
    params.push(`caption=${encodeURIComponent(uploadOptions.caption)}`);
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, `${productImagesPath(productId)}?${params.join('&')}`);

  const uploaded = await uploadFile(url, blob, uploadOptions.contentType, {
    fetchImpl: options.fetchImpl,
    signal: options.signal,
  });
  if (uploaded.status !== 'ready') {
    return uploaded;
  }

  const failure = await productImageFailureFromResponse(uploaded.response);
  if (failure !== null) {
    return failure;
  }

  let payload: unknown;
  try {
    payload = await uploaded.response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const image = parseProductImage(payload);
  return image === null ? { status: 'invalid-response' } : { status: 'ready', image };
}

/**
 * `GET .../images/{image_id}/content` — ไบต์ของรูปจริง ต้องแนบ token เสมอ
 *
 * ไม่ใช้ `downloadFile()`: ฟังก์ชันนั้นสร้าง-revoke object URL เองเพื่อสั่งเซฟไฟล์
 * ซึ่งตรงข้ามกับที่ต้องการที่นี่ — เส้นนี้ต้องคืน `Blob` ดิบให้ผู้เรียกไปสร้าง/revoke
 * object URL เองตามจังหวะที่ UI ต้องใช้ (`<img>` แสดงผล ไม่ใช่สั่งดาวน์โหลด)
 */
export async function fetchProductImageBlob(
  productId: string,
  imageId: string,
  options: ProductRequestOptions = {},
): Promise<ProductImageBlobResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, productImageContentPath(productId, imageId));

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    default:
      break;
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  const blob = await response.blob();
  const contentType = response.headers.get('content-type') ?? '';
  return { status: 'ready', blob, contentType };
}

/** `POST .../images/{image_id}/primary` — ตั้งเป็นรูปหลัก */
export async function setPrimaryProductImage(
  productId: string,
  imageId: string,
  options: ProductRequestOptions = {},
): Promise<ProductImageResult> {
  const result = await requestPayload(
    productImagePrimaryPath(productId, imageId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const image = parseProductImage(result.payload);
  return image === null ? { status: 'invalid-response' } : { status: 'ready', image };
}

/** `POST .../images/{image_id}/remove` — ปลดสถานะ ACTIVE ของรูป (soft remove) */
export async function removeProductImage(
  productId: string,
  imageId: string,
  options: ProductRequestOptions = {},
): Promise<ProductImageResult> {
  const result = await requestPayload(
    productImageRemovePath(productId, imageId),
    { method: 'POST', headers: { Accept: 'application/json' } },
    options,
  );
  if (result.status !== 'payload') {
    return result;
  }

  const image = parseProductImage(result.payload);
  return image === null ? { status: 'invalid-response' } : { status: 'ready', image };
}
